import React, { useState, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Paper,
  Link,
  CircularProgress,
  Snackbar,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { authApi } from "../apis/authApi";

interface NableSetupFormProps {
  onBack?: () => void;
  onSuccess?: () => void;
  onSkip?: () => void;
}

const NableSetupForm: React.FC<NableSetupFormProps> = ({ onBack, onSuccess, onSkip }) => {
  const [apiKey, setApiKey] = useState("");
  const [baseUrl, setBaseUrl] = useState("");
  const [isTesting, setIsTesting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [testResult, setTestResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  
  const testedValuesRef = useRef<{
    apiKey: string;
    baseUrl: string;
  } | null>(null);
  
  const hasFieldsChanged = () => {
    if (!testedValuesRef.current || !testResult?.success) {
      return true;
    }
    return (
      apiKey !== testedValuesRef.current.apiKey ||
      baseUrl !== testedValuesRef.current.baseUrl
    );
  };
  
  const isSaveDisabled = !testResult?.success || hasFieldsChanged() || isSaving;

  const handleTestConnection = async () => {
    if (!apiKey || !baseUrl) {
      setTestResult({
        success: false,
        message: "Please fill in all fields before testing the connection.",
      });
      return;
    }

    setIsTesting(true);
    setTestResult(null);

    try {
      const result = await authApi.testNableConnection({
        api_key: apiKey,
        base_url: baseUrl,
      });

      if (result.success) {
        testedValuesRef.current = { apiKey, baseUrl };
      }

      setTestResult({
        success: result.success,
        message: result.message || "Connection test successful!",
      });
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || "Connection test failed. Please check your credentials.";
      setTestResult({
        success: false,
        message: errorMessage,
      });
      testedValuesRef.current = null;
    } finally {
      setIsTesting(false);
    }
  };

  const handleSave = async () => {
    if (isSaveDisabled) {
      return;
    }

    setIsSaving(true);

    try {
      await authApi.saveNableConfiguration({
        api_key: apiKey,
        base_url: baseUrl,
      });

      setShowSuccessSnackbar(true);
      
      if (onSuccess) {
        setTimeout(() => {
          onSuccess();
        }, 1500);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || "Failed to save Nable configuration.";
      setTestResult({
        success: false,
        message: errorMessage,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box>
      {onBack && (
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          sx={{
            fontFamily: "var(--theo-font-sans)",
            color: "#6c6c6c",
            textTransform: "none",
            mb: 3,
            "&:hover": {
              background: "#f5f5f5",
            },
          }}
        >
          Back
        </Button>
      )}

      <Typography
        variant="h5"
        component="h2"
        sx={{
          fontFamily: "var(--theo-font-sans)",
          fontWeight: 700,
          mb: 3,
          color: "#1a1a1a",
        }}
      >
        N-able Setup
      </Typography>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 4,
          background: "#f8fafc",
          borderRadius: 2,
          border: "1px solid #e5e5e5",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontFamily: "var(--theo-font-sans)",
            fontWeight: 600,
            mb: 2,
            color: "#1a1a1a",
          }}
        >
          How to Get N-able API Credentials
        </Typography>
        <Box
          component="ol"
          sx={{
            fontFamily: "var(--theo-font-sans)",
            color: "#6c6c6c",
            pl: 3,
            "& li": {
              mb: 1.5,
              lineHeight: 1.6,
            },
          }}
        >
          <li>
            Log in to your N-able account as an administrator.
          </li>
          <li>
            Navigate to <strong>Settings &gt; API Access</strong>.
          </li>
          <li>
            Generate a new API key with appropriate permissions.
          </li>
          <li>
            Copy the API key and your instance base URL.
          </li>
          <li>
            For more information, visit the{" "}
            <Link
              href="https://documentation.n-able.com/"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: "#705d4a" }}
            >
              N-able API documentation
            </Link>
            .
          </li>
        </Box>
      </Paper>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <TextField
          label="Base URL"
          value={baseUrl}
          onChange={(e) => {
            setBaseUrl(e.target.value);
            if (testResult?.success) {
              setTestResult(null);
            }
          }}
          placeholder="https://your-instance.n-able.com"
          fullWidth
          required
          sx={{
            fontFamily: "var(--theo-font-sans)",
            "& .MuiInputLabel-root": {
              fontFamily: "var(--theo-font-sans)",
            },
            "& .MuiInputBase-input": {
              fontFamily: "var(--theo-font-sans)",
            },
          }}
          helperText="Your N-able instance URL"
        />

        <TextField
          label="API Key"
          type="password"
          value={apiKey}
          onChange={(e) => {
            setApiKey(e.target.value);
            if (testResult?.success) {
              setTestResult(null);
            }
          }}
          fullWidth
          required
          sx={{
            fontFamily: "var(--theo-font-sans)",
            "& .MuiInputLabel-root": {
              fontFamily: "var(--theo-font-sans)",
            },
            "& .MuiInputBase-input": {
              fontFamily: "var(--theo-font-sans)",
            },
          }}
          helperText="Your N-able API key"
        />

        {testResult && (
          <Alert
            severity={testResult.success ? "success" : "error"}
            sx={{
              fontFamily: "var(--theo-font-sans)",
              "& .MuiAlert-message": {
                fontFamily: "var(--theo-font-sans)",
              },
            }}
          >
            {testResult.message}
          </Alert>
        )}

        <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
          <Button
            variant="outlined"
            onClick={handleTestConnection}
            disabled={isTesting || !apiKey || !baseUrl}
            sx={{
              fontFamily: "var(--theo-font-sans)",
              textTransform: "none",
              fontWeight: 500,
              borderColor: "#705d4a",
              color: "#705d4a",
              "&:hover": {
                borderColor: "#604d3a",
                background: "#f5f5f5",
              },
              minWidth: 150,
            }}
          >
            {isTesting ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Testing...
              </>
            ) : (
              "Test Connection"
            )}
          </Button>

          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isSaveDisabled}
            sx={{
              fontFamily: "var(--theo-font-sans)",
              textTransform: "none",
              fontWeight: 500,
              background: isSaveDisabled ? "#e0e0e0" : "#705d4a",
              color: isSaveDisabled ? "#9e9e9e" : "#fff",
              minWidth: 150,
              "&:hover": {
                background: isSaveDisabled ? "#e0e0e0" : "#604d3a",
              },
            }}
          >
            {isSaving ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Saving...
              </>
            ) : (
              "Save"
            )}
          </Button>

          {onSkip && (
            <Button
              variant="text"
              onClick={onSkip}
              sx={{
                fontFamily: "var(--theo-font-sans)",
                textTransform: "none",
                fontWeight: 500,
                color: "#6c6c6c",
                "&:hover": {
                  background: "#f5f5f5",
                },
              }}
            >
              Skip for now
            </Button>
          )}
        </Box>
      </Box>

      <Snackbar
        open={showSuccessSnackbar}
        autoHideDuration={3000}
        onClose={() => setShowSuccessSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setShowSuccessSnackbar(false)}
          severity="success"
          sx={{ fontFamily: "var(--theo-font-sans)" }}
        >
          N-able has been successfully connected!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NableSetupForm;
